// src/tabs/LigasSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Hammer, Scale, Trash2, Plus, Percent, Layers, ShieldAlert, ArrowRight, Save, Copy } from "lucide-react";
import { pb } from "../services/pocketbase";
import { suppliesService } from "../services/suppliesService";

const getSupplyUnitCost = (supply) => {
  return suppliesService.getSupplyUnitCost(supply);
};

export default function LigasSubTab({ user }) {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState("");
  const [metalsSupplies, setMetalsSupplies] = useState([]);
  
  // Calculation inputs
  const [modo, setModo] = useState(() => localStorage.getItem("liga_modo") || "peso_final"); // "peso_final" | "metal_principal"
  const [pesoEntrada, setPesoEntrada] = useState(() => localStorage.getItem("liga_peso_entrada") || "10");
  const [metalPrincipalId, setMetalPrincipalId] = useState("");

  // Editor states
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [editorName, setEditorName] = useState("");
  const [editorObs, setEditorObs] = useState("");
  const [editorComponents, setEditorComponents] = useState([]);

  // Load recipes and supplies
  const loadInitialData = useCallback(async () => {
    try {
      // 1. Carrega insumos de metal
      const supplies = await pb.collection("supplies").getFullList({
        filter: "category='metal' || sector='oficina' && name~'Ouro' || sector='oficina' && name~'Prata'"
      });
      setMetalsSupplies(supplies);
      // 2. Carrega receitas de ligas
      const alloyRecipes = await pb.collection("ligas_receitas").getFullList({ sort: "nome" });
      setRecipes(alloyRecipes);
      if (alloyRecipes.length > 0) {
        const lastSelected = localStorage.getItem("liga_recipe_id");
        if (lastSelected && alloyRecipes.some(r => r.id === lastSelected)) {
          setSelectedRecipeId(lastSelected);
        } else {
          setSelectedRecipeId(alloyRecipes[0].id);
        }
      }
    } catch (e) {
      console.error("Erro loading liga data:", e);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Persist preferences
  useEffect(() => {
    localStorage.setItem("liga_modo", modo);
  }, [modo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem("liga_peso_entrada", pesoEntrada);
    }, 1200);
    return () => clearTimeout(timer);
  }, [pesoEntrada]);

  useEffect(() => {
    if (selectedRecipeId) {
      localStorage.setItem("liga_recipe_id", selectedRecipeId);
    }
  }, [selectedRecipeId]);

  // Selected Recipe
  const activeRecipe = useMemo(() => recipes.find(r => r.id === selectedRecipeId), [selectedRecipeId, recipes]);

  // Set default metal principal when recipe changes
  useEffect(() => {
    if (activeRecipe && activeRecipe.componentes && activeRecipe.componentes.length > 0) {
      // Procura o componente com maior percentual
      const sorted = [...activeRecipe.componentes].sort((a,b) => b.percentual - a.percentual);
      setMetalPrincipalId(sorted[0].insumo_id || sorted[0].insumo_nome);
    }
  }, [activeRecipe]);

  // Calculations
  const calculatedResult = useMemo(() => {
    if (!activeRecipe || !activeRecipe.componentes) return null;

    const pesoInput = parseFloat(pesoEntrada) || 0;
    const comps = activeRecipe.componentes;

    let totalWeight = 0;

    if (modo === "peso_final") {
      totalWeight = pesoInput;
    } else {
      // modo metal_principal
      const principalComp = comps.find(c => (c.insumo_id === metalPrincipalId || c.insumo_nome === metalPrincipalId));
      if (principalComp && principalComp.percentual > 0) {
        totalWeight = pesoInput / (principalComp.percentual / 100);
      }
    }

    if (totalWeight <= 0) return null;

    let calculatedCost = 0;
    let totalDensityProduct = 0;

    const mappedComps = comps.map(c => {
      const weight = totalWeight * (c.percentual / 100);
      
      // Achar custo real no metalSupplies
      const matchSupply = metalsSupplies.find(s => s.id === c.insumo_id || s.name === c.insumo_nome);
      const costPerGram = matchSupply ? getSupplyUnitCost(matchSupply) : 0;
      const totalCost = weight * costPerGram;

      calculatedCost += totalCost;
      totalDensityProduct += weight * (c.densidade_aproximada || 10);

      return {
        id: c.insumo_id,
        nome: c.insumo_nome,
        simbolo: c.simbolo,
        percentual: c.percentual,
        peso: Math.round(weight * 1000) / 1000,
        custo: Math.round(totalCost * 100) / 100,
        custoUnitario: costPerGram
      };
    });

    const finalCostPerGram = calculatedCost / totalWeight;
    const finalDensity = totalDensityProduct / totalWeight;

    return {
      pesoTotal: Math.round(totalWeight * 1000) / 1000,
      custoTotal: Math.round(calculatedCost * 100) / 100,
      custoPorGrama: Math.round(finalCostPerGram * 100) / 100,
      densidadeTotal: Math.round(finalDensity * 100) / 100,
      componentes: mappedComps
    };
  }, [activeRecipe, modo, pesoEntrada, metalPrincipalId, metalsSupplies]);

  // Save calculation
  const handleSaveCalculation = async () => {
    if (!activeRecipe || !calculatedResult) return;
    try {
      await pb.collection("calculos_liga").create({
        receita_id: activeRecipe.id,
        receita_nome: activeRecipe.nome,
        modo,
        peso_entrada: parseFloat(pesoEntrada) || 0,
        metal_principal_nome: modo === "metal_principal" ? metalPrincipalId : "",
        resultado: calculatedResult
      });
      alert("Cálculo da liga salvo no histórico!");
    } catch (e) {
      alert("Erro ao salvar cálculo: " + e.message);
    }
  };

  // Export to pricing calculator
  const handleExportToPriceCalculator = () => {
    if (!calculatedResult || !activeRecipe) return;
    const exportData = {
      ligaId: activeRecipe.id,
      ligaNome: activeRecipe.nome,
      custoPorGrama: calculatedResult.custoPorGrama,
      densidade: calculatedResult.densidadeTotal
    };
    localStorage.setItem("ligas_exported_calc", JSON.stringify(exportData));
    alert(`Informações da liga "${activeRecipe.nome}" exportadas para a Calculadora de Preço!\n\nCusto: R$ ${calculatedResult.custoPorGrama.toFixed(2)} /g`);
  };

  // Custom Recipe Editor Grid actions
  const handleOpenNewRecipe = () => {
    setEditingRecipe(null);
    setEditorName("");
    setEditorObs("");
    setEditorComponents([
      { id: Date.now().toString(), insumo_id: "", insumo_nome: "", percentual: 50, densidade_aproximada: 10, simbolo: "" }
    ]);
    setIsEditorOpen(true);
  };

  const handleOpenEditRecipe = () => {
    if (!activeRecipe) return;
    setEditingRecipe(activeRecipe);
    setEditorName(activeRecipe.nome);
    setEditorObs(activeRecipe.observacoes || "");
    setEditorComponents(activeRecipe.componentes.map((c, idx) => ({
      id: idx.toString(),
      insumo_id: c.insumo_id || "",
      insumo_nome: c.insumo_nome || "",
      percentual: c.percentual,
      densidade_aproximada: c.densidade_aproximada || 10,
      simbolo: c.simbolo || ""
    })));
    setIsEditorOpen(true);
  };

  const handleDeleteRecipe = async () => {
    if (!selectedRecipeId) return;
    const recipe = recipes.find(r => r.id === selectedRecipeId);
    if (!recipe) return;
    if (!window.confirm(`Deseja realmente excluir a receita de liga "${recipe.nome}"?`)) return;

    try {
      await pb.collection("ligas_receitas").delete(recipe.id);
      const remaining = recipes.filter(r => r.id !== recipe.id);
      setRecipes(remaining);
      if (remaining.length > 0) {
        setSelectedRecipeId(remaining[0].id);
      } else {
        setSelectedRecipeId("");
      }
      alert("Receita de liga excluída com sucesso!");
    } catch (err) {
      alert("Erro ao excluir receita: " + err.message);
    }
  };

  const handleDuplicateRecipe = () => {
    if (!activeRecipe) return;
    setEditingRecipe(null);
    setEditorName(`${activeRecipe.nome} (Cópia)`);
    setEditorObs(activeRecipe.observacoes || "");
    setEditorComponents(activeRecipe.componentes.map((c, idx) => ({
      id: idx.toString(),
      insumo_id: c.insumo_id || "",
      insumo_nome: c.insumo_nome || "",
      percentual: c.percentual,
      densidade_aproximada: c.densidade_aproximada || 10,
      simbolo: c.simbolo || ""
    })));
    setIsEditorOpen(true);
  };

  const handleAddEditorComponent = () => {
    setEditorComponents(prev => [
      ...prev,
      { id: Date.now().toString(), insumo_id: "", insumo_nome: "", percentual: 0, densidade_aproximada: 10, simbolo: "" }
    ]);
  };

  const handleRemoveEditorComponent = (id) => {
    if (editorComponents.length === 1) return;
    setEditorComponents(prev => prev.filter(c => c.id !== id));
  };

  const handleEditorComponentChange = (id, field, value) => {
    setEditorComponents(prev =>
      prev.map(c => {
        if (c.id === id) {
          if (field === "insumo_id") {
            const match = metalsSupplies.find(s => s.id === value);
            // Deduze símbolo e densidade se conhecidos
            let symbol = "";
            let density = 10.0;
            const name = match ? match.name.toLowerCase() : "";
            if (name.includes("prata")) { symbol = "Ag"; density = 10.49; }
            else if (name.includes("cobre")) { symbol = "Cu"; density = 8.96; }
            else if (name.includes("ouro")) { symbol = "Au"; density = 19.3; }
            else if (name.includes("paladio")) { symbol = "Pd"; density = 12.02; }
            else if (name.includes("platina")) { symbol = "Pt"; density = 21.45; }
            else if (name.includes("iridio")) { symbol = "Ir"; density = 22.56; }
            
            return {
              ...c,
              insumo_id: value,
              insumo_nome: match ? match.name : "",
              simbolo: symbol,
              densidade_aproximada: density
            };
          }
          return { ...c, [field]: value };
        }
        return c;
      })
    );
  };

  const editorSumPercent = useMemo(() => {
    return editorComponents.reduce((sum, c) => sum + (parseFloat(c.percentual) || 0), 0);
  }, [editorComponents]);

  const handleSaveRecipe = async (e) => {
    e.preventDefault();
    if (!editorName.trim()) return alert("Digite o nome da receita.");
    
    // Validar percentuais
    if (Math.abs(editorSumPercent - 100) > 0.1) {
      return alert(`A soma dos percentuais é ${editorSumPercent}%. Ela deve ser exatamente 100%.`);
    }

    // Validar insumos
    for (const comp of editorComponents) {
      if (!comp.insumo_id) return alert("Selecione todos os componentes metálicos.");
    }

    try {
      const payload = {
        nome: editorName.trim(),
        tipo: "customizado",
        observacoes: editorObs.trim(),
        componentes: editorComponents.map(c => ({
          insumo_id: c.insumo_id,
          insumo_nome: c.insumo_nome,
          simbolo: c.simbolo || "M",
          percentual: Number(c.percentual),
          densidade_aproximada: Number(c.densidade_aproximada) || 10
        }))
      };

      let record;
      if (editingRecipe) {
        record = await pb.collection("ia_configs").update(editingRecipe.id, payload); // Wait, no! We need to update ligas_receitas!
      }
      
      // Let's call the correct collection
      if (editingRecipe) {
        record = await pb.collection("ligas_receitas").update(editingRecipe.id, payload);
        setRecipes(prev => prev.map(r => r.id === record.id ? record : r));
      } else {
        record = await pb.collection("ligas_receitas").create(payload);
        setRecipes(prev => [...prev, record].sort((a,b) => a.nome.localeCompare(b.nome)));
      }

      setSelectedRecipeId(record.id);
      setIsEditorOpen(false);
      alert("Receita de liga salva com sucesso!");
    } catch (err) {
      alert("Erro ao salvar receita de liga: " + err.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50">
      
      {/* LEFT: CALCULATOR */}
      <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Hammer className="text-blue-500" /> Calculadora de Ligas Metálicas
              </h2>
              <p className="text-xs text-slate-500">Cálculo de componentes para fundição baseado em receitas e metal base</p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenNewRecipe}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1"
              >
                <Plus size={14} /> Nova Receita
              </button>
              {activeRecipe && (
                <button
                  onClick={handleDuplicateRecipe}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-250 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                >
                  <Copy size={14} /> Duplicar
                </button>
              )}
              {activeRecipe && (
                <>
                  <button
                    onClick={handleOpenEditRecipe}
                    className="bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handleDeleteRecipe}
                    className="bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                  >
                    Excluir
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Escolha Liga */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Escolha a Receita de Liga
              </label>
              <select
                value={selectedRecipeId}
                onChange={(e) => setSelectedRecipeId(e.target.value)}
                className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
              >
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome} {r.tipo === "padrao" ? "(Padrão)" : "(Customizada)"}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggle de Modo */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Modo de Entrada
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setModo("peso_final")}
                  className={`p-2.5 rounded-xl border text-xs font-bold uppercase transition-all ${
                    modo === "peso_final"
                      ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                      : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  Peso Final Desejado
                </button>
                <button
                  onClick={() => setModo("metal_principal")}
                  className={`p-2.5 rounded-xl border text-xs font-bold uppercase transition-all ${
                    modo === "metal_principal"
                      ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                      : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  Metal Principal
                </button>
              </div>
            </div>

            {/* Peso Input */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                {modo === "peso_final" ? "Peso Final Desejado (g)" : "Peso do Metal Principal (g)"}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0.1"
                  step="any"
                  className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold font-mono"
                  value={pesoEntrada}
                  onChange={(e) => setPesoEntrada(e.target.value)}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">g</span>
              </div>
            </div>

            {/* Metal Principal Dropdown (Se modo principal) */}
            {modo === "metal_principal" && activeRecipe?.componentes && (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Componente de Referência
                </label>
                <select
                  value={metalPrincipalId}
                  onChange={(e) => setMetalPrincipalId(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
                >
                  {activeRecipe.componentes.map((c) => (
                    <option key={c.insumo_id || c.insumo_nome} value={c.insumo_id || c.insumo_nome}>
                      {c.insumo_nome} ({c.simbolo}) — {c.percentual}%
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* METRICS RESULTS */}
        {calculatedResult && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900 rounded-2xl p-4 text-center text-white border border-slate-800">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Peso Total Liga</span>
              <span className="text-xl font-extrabold font-mono text-blue-400">{calculatedResult.pesoTotal} g</span>
            </div>

            <div className="bg-slate-900 rounded-2xl p-4 text-center text-white border border-slate-800">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Custo Total Metais</span>
              <span className="text-xl font-extrabold font-mono text-emerald-400">R$ {calculatedResult.custoTotal.toFixed(2)}</span>
            </div>

            <div className="bg-slate-900 rounded-2xl p-4 text-center text-white border border-slate-800">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Custo por Grama</span>
              <span className="text-xl font-extrabold font-mono text-yellow-400">R$ {calculatedResult.custoPorGrama.toFixed(2)} /g</span>
            </div>

            <div className="bg-slate-900 rounded-2xl p-4 text-center text-white border border-slate-800">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Densidade Média</span>
              <span className="text-xl font-extrabold font-mono text-purple-400">{calculatedResult.densidadeTotal} g/cm³</span>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSaveCalculation}
            disabled={!calculatedResult}
            className="flex-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold p-3 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-xs disabled:opacity-50"
          >
            <Save size={16} /> Registrar Histórico
          </button>
          
          <button
            onClick={handleExportToPriceCalculator}
            disabled={!calculatedResult}
            className="flex-1 bg-blue-600 hover:bg-blue-750 text-white font-bold p-3 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-xs disabled:opacity-50"
          >
            Exportar para Calc. Preço <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* RIGHT: DETAILED COMPONENTS TABLE */}
      <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0 p-4 overflow-y-auto custom-scrollbar">
        <h3 className="font-bold text-slate-700 flex items-center gap-1.5 text-sm mb-4 border-b border-slate-100 pb-3 shrink-0">
          <Layers size={16} className="text-blue-500" /> Composição Calculada
        </h3>

        {calculatedResult ? (
          <div className="space-y-4">
            {calculatedResult.componentes.map((comp) => {
              const isCostZero = comp.custoUnitario === 0;

              return (
                <div
                  key={comp.id || comp.nome}
                  className={`p-3.5 rounded-xl border flex flex-col gap-2 ${
                    isCostZero ? "bg-red-50/20 border-red-200" : "bg-slate-50/50 border-slate-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-extrabold text-xs text-slate-800 flex items-center gap-1.5">
                      <span className="px-2 py-0.5 bg-slate-800 text-white rounded font-mono text-[10px]">{comp.simbolo}</span>
                      {comp.nome}
                    </span>
                    <span className="text-xs font-bold text-slate-500">{comp.percentual}%</span>
                  </div>

                  <div className="flex justify-between text-[11px] font-semibold text-slate-500 pt-1.5 border-t border-slate-200/50">
                    <span>Peso: <strong className="text-slate-800 font-mono text-xs">{comp.peso} g</strong></span>
                    <span className="text-right">
                      Custo: <strong className="text-slate-800 font-mono text-xs">R$ {comp.custo.toFixed(2)}</strong>
                    </span>
                  </div>

                  {isCostZero && (
                    <div className="mt-1 bg-red-100 text-red-800 rounded p-1.5 text-[9px] font-bold flex items-center gap-1 leading-tight">
                      <ShieldAlert size={12} className="shrink-0" />
                      Insumo sem custo cadastrado! Acesse a aba Insumos e preencha.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-xs font-semibold text-center">
            Aguardando pesos válidos.
          </div>
        )}
      </div>

      {/* RECIPE EDITOR MODAL */}
      {isEditorOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-bold text-slate-800">
                {editingRecipe ? "Editar Receita de Liga" : "Criar Receita de Liga"}
              </h3>
              <button onClick={() => setIsEditorOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleSaveRecipe} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome da Receita *</label>
                <input
                  type="text" required
                  value={editorName}
                  onChange={(e) => setEditorName(e.target.value)}
                  placeholder="Ex: Ouro 18k Rose Custom"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                <textarea
                  value={editorObs}
                  onChange={(e) => setEditorObs(e.target.value)}
                  rows={2}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-xs"
                />
              </div>

              {/* COMPONENTS EDITOR GRID */}
              <div>
                <div className="flex justify-between items-center mb-2 border-b border-slate-100 pb-1.5">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">Metais Componentes</label>
                  <button
                    type="button"
                    onClick={handleAddEditorComponent}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700"
                  >
                    + Metal
                  </button>
                </div>

                <div className="space-y-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200 max-h-48 overflow-y-auto custom-scrollbar">
                  {editorComponents.map((comp) => (
                    <div key={comp.id} className="flex gap-2 items-center bg-white p-2 rounded-lg border border-slate-200 shadow-sm text-xs">
                      {/* Metal select */}
                      <select
                        required
                        value={comp.insumo_id}
                        onChange={(e) => handleEditorComponentChange(comp.id, "insumo_id", e.target.value)}
                        className="flex-1 p-1.5 border border-slate-300 rounded outline-none focus:border-blue-500 font-semibold bg-white"
                      >
                        <option value="" disabled>Selecione o metal...</option>
                        {metalsSupplies.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>

                      {/* Percent */}
                      <div className="flex items-center gap-1 w-24">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="any"
                          required
                          value={comp.percentual}
                          onChange={(e) => handleEditorComponentChange(comp.id, "percentual", e.target.value)}
                          className="w-16 p-1.5 border border-slate-300 rounded font-mono font-bold text-center"
                        />
                        <span className="text-[10px] text-slate-400 font-black"><Percent size={12} /></span>
                      </div>

                      {/* Density info */}
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          step="any"
                          value={comp.densidade_aproximada}
                          onChange={(e) => handleEditorComponentChange(comp.id, "densidade_aproximada", e.target.value)}
                          className="w-full p-1.5 border border-slate-300 rounded font-mono text-[10px] text-center"
                          title="Densidade aproximada do metal (g/cm³)"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveEditorComponent(comp.id)}
                        className="p-1 text-slate-400 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-2.5 flex justify-between items-center text-xs font-bold px-1.5">
                  <span className="text-slate-500">Soma Total:</span>
                  <span className={`${Math.abs(editorSumPercent - 100) <= 0.1 ? "text-emerald-600" : "text-red-500"}`}>
                    {editorSumPercent}% (deve ser 100%)
                  </span>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Salvar Receita
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
